import {I18n} from 'aws-amplify/utils';
import {
    ActionIcon,
    Alert,
    Button,
    Group,
    NumberInput,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';

import {RecordFormValues} from './recordFormValues';

// FNP-1192 — the record form's fields.
//
// Presentational: every value and handler arrives as a prop. The validation
// lives in `recordValidation`, the submit plumbing in `RecordForm`.

export const RecordFormComponent = (
    {
        values,
        errors,
        supportedRecordTypes,
        supportedTypesAreProviderReported,
        isEdit,
        onChange,
    }: {
        values: RecordFormValues,
        // Keyed by field, plus `values.<index>` for a specific value row.
        errors: Record<string, string>,
        supportedRecordTypes: string[],
        supportedTypesAreProviderReported: boolean,
        isEdit: boolean,
        onChange: (next: Partial<RecordFormValues>) => void,
    },
) => {

    const updateValueAt = (index: number, next: string) => {
        const copy = [...values.values];
        copy[index] = next;
        onChange({values: copy});
    };

    const removeValueAt = (index: number) => {
        onChange({values: values.values.filter((_, i) => i !== index)});
    };

    return (
        <Stack gap={'md'}>

            <TextInput
                label={I18n.get('Name')}
                description={I18n.get('The fully-qualified record name, without a trailing dot.')}
                placeholder={'www.example.com'}
                value={values.name}
                error={errors.name}
                onChange={event => onChange({name: event.currentTarget.value})}
                required={true}
            />

            {/* ⚠️ THE PICKER OFFERS ONLY WHAT THE PROVIDER SUPPORTS.
                `supportedRecordTypes` comes from the connection's capability
                snapshot, so a type this provider would reject is never offered
                — the rejection is prevented rather than explained. */}
            <Select
                label={I18n.get('Type')}
                description={supportedTypesAreProviderReported
                    ? I18n.get('Only the record types this provider supports are offered.')
                    : I18n.get('This connection did not report which record types it supports, so a common set is offered. The provider may still refuse one.')}
                data={supportedRecordTypes}
                value={values.type}
                error={errors.type}
                onChange={next => onChange({type: next ?? ''})}
                required={true}
                searchable={true}
                allowDeselect={false}
            />

            <NumberInput
                label={I18n.get('Time to live (seconds)')}
                description={I18n.get('How long resolvers may cache this record. A change is not visible everywhere until the PREVIOUS value\'s time to live has expired.')}
                value={values.ttlSeconds}
                error={errors.ttlSeconds}
                onChange={next => onChange({
                    ttlSeconds: typeof next === 'number' ? next : Number(next),
                })}
                min={0}
                max={604800}
                required={true}
            />

            <Stack gap={'xs'}>
                <Text size={'sm'} fw={500}>{I18n.get('Values')}</Text>
                <Text size={'xs'} c={'dimmed'}>
                    {I18n.get('What this record answers with. Several values are allowed — an MX or TXT record often has more than one.')}
                </Text>

                {values.values.map((value, index) => (
                    <Group key={index} gap={'xs'} align={'flex-start'} wrap={'nowrap'}>
                        {/* A Textarea rather than a TextInput: DKIM keys and
                            long TXT values are routinely longer than one line,
                            and a single-line input makes them unreadable. */}
                        <Textarea
                            style={{flex: 1}}
                            autosize={true}
                            minRows={1}
                            maxRows={4}
                            value={value}
                            error={errors[`values.${index}`]}
                            onChange={event => updateValueAt(index, event.currentTarget.value)}
                            placeholder={I18n.get('Value')}
                        />
                        {values.values.length > 1 && (
                            <ActionIcon
                                variant={'subtle'}
                                color={'red'}
                                onClick={() => removeValueAt(index)}
                                aria-label={I18n.get('Remove value')}
                                mt={4}
                            >
                                ×
                            </ActionIcon>
                        )}
                    </Group>
                ))}

                <Group>
                    <Button
                        variant={'subtle'}
                        size={'xs'}
                        onClick={() => onChange({values: [...values.values, '']})}
                    >
                        {I18n.get('Add another value')}
                    </Button>
                </Group>
            </Stack>

            {/* ⚠️ AN EDIT THAT CHANGES NAME OR TYPE IS A MOVE, NOT AN EDIT, and
                the operator is told so before they submit. A record's identity
                IS (name, type); changing either deletes the old record and
                creates a new one, which is a different act from changing a
                value and deserves to be named. */}
            {isEdit && (values.name !== values.originalName || values.type !== values.originalType) && (
                <Alert color={'yellow'} variant={'light'} title={I18n.get('This will move the record')}>
                    {I18n.get('A record is identified by its name and type. Changing either removes')}{' '}
                    <Text span={true} fw={600}>{values.originalName} {values.originalType}</Text>{' '}
                    {I18n.get('and creates')}{' '}
                    <Text span={true} fw={600}>{values.name} {values.type}</Text>{' '}
                    {I18n.get('in one change.')}
                </Alert>
            )}

        </Stack>
    );
};
